import { useEffect, useRef, useState } from "react";

import { Icon } from "./Icon";
import "./PeriodPicker.css";

/** Intervalo por DIA (DD/MM/AAAA). Qualquer um dos limites é opcional:
 *  só início, só fim, ou ambos. `null` (ou ambos null) = sem filtro. */
export type Period = { de: string | null; ate: string | null } | null;

const MES_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const WKD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const isoToBr = (s: string | null) => {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

function chipLabel(value: Period): string {
  const de = value?.de ?? null;
  const ate = value?.ate ?? null;
  if (!de && !ate) return "Selecione…";
  if (de && ate) return `${isoToBr(de)} – ${isoToBr(ate)}`;
  if (de) return `a partir de ${isoToBr(de)}`;
  return `até ${isoToBr(ate)}`;
}

/** Seletor de período por dia. Chip → popover com calendário. Define início e/ou
 *  fim (ambos opcionais) e confirma em "Aplicar". */
export function PeriodPicker({
  value,
  onChange,
  label = "Competência",
}: {
  value: Period;
  onChange: (p: Period) => void;
  label?: string;
}) {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [de, setDe] = useState<string | null>(value?.de ?? null);
  const [ate, setAte] = useState<string | null>(value?.ate ?? null);
  const [active, setActive] = useState<"de" | "ate">("de");
  const [viewY, setViewY] = useState(today.getFullYear());
  const [viewM, setViewM] = useState(today.getMonth() + 1); // 1-12
  const ref = useRef<HTMLDivElement>(null);

  // Sincroniza o rascunho com o valor externo ao abrir.
  useEffect(() => {
    if (!open) return;
    setDe(value?.de ?? null);
    setAte(value?.ate ?? null);
    setActive("de");
    const anchor = value?.de ?? value?.ate;
    if (anchor) {
      const [y, m] = anchor.split("-");
      setViewY(Number(y));
      setViewM(Number(m));
    }
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, value]);

  const prevMonth = () => {
    if (viewM === 1) {
      setViewM(12);
      setViewY((y) => y - 1);
    } else {
      setViewM((m) => m - 1);
    }
  };
  const nextMonth = () => {
    if (viewM === 12) {
      setViewM(1);
      setViewY((y) => y + 1);
    } else {
      setViewM((m) => m + 1);
    }
  };

  const pickDay = (d: number) => {
    const picked = iso(viewY, viewM, d);
    if (active === "de") {
      setDe(picked);
      setActive("ate");
    } else {
      setAte(picked);
    }
  };

  const apply = () => {
    let d = de;
    let a = ate;
    if (d && a && d > a) [d, a] = [a, d]; // ordena se invertido
    onChange(d || a ? { de: d, ate: a } : null);
    setOpen(false);
  };

  const clearAll = () => {
    setDe(null);
    setAte(null);
    onChange(null);
    setOpen(false);
  };

  const firstWeekday = new Date(viewY, viewM - 1, 1).getDay(); // 0=dom
  const daysInMonth = new Date(viewY, viewM, 0).getDate();
  const cells: (number | null)[] = [
    ...Array<null>(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div className="period-pick" ref={ref}>
      <button
        type="button"
        className={`period-chip ${value && (value.de || value.ate) ? "has-value" : ""} ${open ? "open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="period-chip-label">{label}</span>
        <span className="period-chip-sep" />
        <span className="period-chip-value">{chipLabel(value)}</span>
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div className="period-pop">
          <div className="period-bounds">
            <button
              type="button"
              className={`period-bound ${active === "de" ? "active" : ""}`}
              onClick={() => setActive("de")}
            >
              <span className="period-bound-k">Início</span>
              <span className="period-bound-v">{isoToBr(de)}</span>
              {de && (
                <span
                  className="period-bound-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDe(null);
                  }}
                >
                  <Icon name="x" size={11} />
                </span>
              )}
            </button>
            <button
              type="button"
              className={`period-bound ${active === "ate" ? "active" : ""}`}
              onClick={() => setActive("ate")}
            >
              <span className="period-bound-k">Fim</span>
              <span className="period-bound-v">{isoToBr(ate)}</span>
              {ate && (
                <span
                  className="period-bound-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAte(null);
                  }}
                >
                  <Icon name="x" size={11} />
                </span>
              )}
            </button>
          </div>

          <div className="period-pop-head">
            <button type="button" className="period-nav" onClick={prevMonth}>
              <Icon name="arrow-left" size={13} />
            </button>
            <span className="period-month">
              {MES_FULL[viewM - 1]} {viewY}
            </span>
            <button type="button" className="period-nav" onClick={nextMonth}>
              <Icon name="arrow-left" size={13} style={{ transform: "rotate(180deg)" }} />
            </button>
          </div>

          <div className="period-cal">
            {WKD.map((w) => (
              <div key={w} className="period-wkd">
                {w}
              </div>
            ))}
            {cells.map((d, i) => {
              if (d == null) return <div key={`e${i}`} className="period-day empty" />;
              const cur = iso(viewY, viewM, d);
              const isDe = cur === de;
              const isAte = cur === ate;
              const inRange = de && ate && cur > de && cur < ate;
              return (
                <button
                  key={cur}
                  type="button"
                  className={`period-day ${isDe || isAte ? "on" : ""} ${inRange ? "in-range" : ""}`}
                  onClick={() => pickDay(d)}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <div className="period-pop-foot">
            <button type="button" className="period-clear" onClick={clearAll}>
              Limpar
            </button>
            <button type="button" className="period-apply" onClick={apply}>
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
